/**
 * @fileoverview PostMessage utilities for communicating with the host.
 */

import { DEFAULT_TIMEOUT, SDK_VERSION, TRUSTED_DOMAINS } from "./constants";
import type { BaseMessage, MessageCallback, Unsubscribe } from "./types";
import { logger } from "./logger";

/**
 * Check if an origin is trusted.
 */
function isTrustedOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    const hostname = url.hostname;

    return TRUSTED_DOMAINS.some((domain) => {
      if (domain.startsWith(".")) {
        // Suffix match (e.g., ".salla.sa" matches "app.salla.sa")
        return hostname.endsWith(domain) || hostname === domain.slice(1);
      }
      // Exact match
      return hostname === domain || hostname.startsWith(`${domain}:`);
    });
  } catch {
    return false;
  }
}

/**
 * Get the parent window (host) reference.
 * Returns null if not in an iframe.
 */
function getParentWindow(): Window | null {
  if (typeof window === "undefined") return null;
  if (window.parent === window) return null;
  return window.parent;
}

/**
 * Send a message to the host (parent window).
 *
 * @param event - The event name
 * @param data - Additional data to send with the message (will be wrapped in payload)
 * @param targetOrigin - Target origin for security (defaults to "*")
 * @param requestId - Optional request ID for request/response patterns
 * @param metadata - Optional metadata for additional context
 */
export function postToHost<T extends Record<string, unknown>>(
  event: string,
  data?: T,
  targetOrigin: string = "*",
  requestId?: string,
  metadata?: Record<string, unknown>,
): void {
  const parent = getParentWindow();

  if (!parent) {
    logger.warn("Not running in an iframe, cannot post to host");
    return;
  }

  const message: BaseMessage = {
    event,
    payload: data || {},
    timestamp: Date.now(),
    source: "embedded-app",
    ...(requestId && { requestId }),
    metadata: {
      version: SDK_VERSION,
      ...(metadata || {}),
    },
  };

  parent.postMessage(message, targetOrigin);
}

/**
 * Message listener registry.
 */
const listeners: Map<string, Set<MessageCallback>> = new Map();

/**
 * Global message handler.
 */
let isListening = false;

function handleMessage(event: MessageEvent): void {
  // Validate origin in production
  if (process.env.NODE_ENV === "production" && !isTrustedOrigin(event.origin)) {
    return;
  }

  const data = event.data as BaseMessage;

  // Validate message structure
  if (
    !data ||
    typeof data.event !== "string" ||
    !data.payload ||
    typeof data.timestamp !== "number" ||
    !data.source
  ) {
    logger.warn("Invalid message structure received:", data);
    return;
  }

  const eventListeners = listeners.get(data.event);
  if (eventListeners) {
    eventListeners.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        logger.error("Error in message handler:", error);
      }
    });
  }

  // Also notify wildcard listeners
  const wildcardListeners = listeners.get("*");
  if (wildcardListeners) {
    wildcardListeners.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        logger.error("Error in wildcard handler:", error);
      }
    });
  }
}

/**
 * Start listening for messages if not already.
 */
function startListening(): void {
  if (isListening || typeof window === "undefined") return;

  window.addEventListener("message", handleMessage);
  isListening = true;
}

/**
 * Subscribe to messages from the host.
 *
 * @param event - The event name to listen for, or "*" for all events
 * @param callback - Callback function when message is received
 * @returns Unsubscribe function
 */
export function onMessage<T extends BaseMessage = BaseMessage>(
  event: string,
  callback: MessageCallback<T>,
): Unsubscribe {
  startListening();

  if (!listeners.has(event)) {
    listeners.set(event, new Set());
  }

  const eventListeners = listeners.get(event)!;
  eventListeners.add(callback as MessageCallback);

  return () => {
    eventListeners.delete(callback as MessageCallback);
    if (eventListeners.size === 0) {
      listeners.delete(event);
    }
  };
}

/**
 * Wait for a specific message from the host.
 *
 * @param event - The event name to wait for
 * @param timeout - Timeout in milliseconds (defaults to DEFAULT_TIMEOUT)
 * @returns Promise that resolves with the message data
 */
export function waitForMessage<T extends BaseMessage = BaseMessage>(
  event: string,
  timeout: number = DEFAULT_TIMEOUT,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error(`[EmbeddedSDK] Timeout waiting for "${event}" message`));
    }, timeout);

    const unsubscribe = onMessage<T>(event, (data) => {
      clearTimeout(timer);
      unsubscribe();
      resolve(data);
    });
  });
}

/**
 * Remove all message listeners.
 */
export function removeAllListeners(): void {
  listeners.clear();

  if (isListening && typeof window !== "undefined") {
    window.removeEventListener("message", handleMessage);
    isListening = false;
  }
}

/**
 * Check if running inside an iframe.
 */
export function isInIframe(): boolean {
  if (typeof window === "undefined") return false;
  return window.parent !== window;
}

/**
 * Dispatch an event locally to registered listeners without sending via postMessage.
 * Used to trigger listeners with synthetic/internal events.
 *
 * @param event - The event name
 * @param payload - The event payload
 * @param requestId - Optional request ID
 */
export function dispatchLocal<T extends Record<string, unknown>>(
  event: string,
  payload: T,
  requestId?: string,
): void {
  const message: BaseMessage = {
    event,
    payload,
    timestamp: Date.now(),
    source: "merchant-dashboard",
    ...(requestId && { requestId }),
    metadata: { version: SDK_VERSION, synthetic: true },
  };

  // Dispatch directly to listeners (origin validation not needed for internal events)
  const eventListeners = listeners.get(event);
  eventListeners?.forEach((callback) => {
    try {
      callback(message);
    } catch (error) {
      logger.error("Error in message handler:", error);
    }
  });
}
